// Posting-rules reminder shown alongside Reddit thread results.
//
// Reuses the same rule store the draft generator uses (`subreddit_rules`:
// paste-first, curated seeds, rate-limited lookups) rather than a separate
// lookup. When a subreddit's rules are already cached we show what they
// actually restrict; otherwise we show the generic caution and link to the
// subreddit's own rules page.

import { useQuery } from "@tanstack/react-query";
import { Info, ShieldAlert } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  classifyConstraints,
  normalizeSubreddit,
  type RuleConstraints,
  type SubredditRule,
} from "@/lib/subreddit-rule-parse";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export const RULES_REMINDER =
  "Before replying: check this subreddit's rules, many restrict links or self-promotion. A disclosed, genuine reply usually works better than a pitch.";

export function rulesUrl(subreddit: string) {
  return `https://www.reddit.com/r/${normalizeSubreddit(subreddit)}/about/rules`;
}

/** Cached rule constraints keyed by lowercased subreddit. Authenticated only. */
export function useSubredditConstraints(subreddits: string[]) {
  const names = Array.from(new Set(subreddits.map(normalizeSubreddit).filter(Boolean)));
  return useQuery({
    queryKey: ["subreddit-constraints", names.slice().sort().join(",")],
    enabled: names.length > 0,
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const { data } = await supabase.rpc("get_subreddit_constraints", {
        _subreddits: names,
      });
      const map = new Map<string, RuleConstraints>();
      for (const row of data ?? []) {
        const rules = Array.isArray(row.rules) ? (row.rules as unknown as SubredditRule[]) : null;
        if (!rules?.length) continue;
        map.set(String(row.subreddit).toLowerCase(), classifyConstraints(rules));
      }
      return map;
    },
  });
}

/** Prominent one-time note above a list of thread results. */
export function RulesReminderBanner() {
  return (
    <div className="flex items-start gap-2.5 rounded-xl border border-signal-amber/30 bg-signal-amber/5 px-4 py-3 text-[12px] leading-snug text-ink">
      <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-signal-amber-dark" />
      <p>{RULES_REMINDER}</p>
    </div>
  );
}

/**
 * Per-thread chip. Shows the real restrictions when this subreddit's rules are
 * already stored, and always links out to the subreddit's rules page.
 */
export function SubredditRulesChip({
  subreddit,
  constraints,
}: {
  subreddit: string;
  constraints?: RuleConstraints | undefined;
}) {
  const known: string[] = [];
  if (constraints) {
    if (!constraints.allow_links) known.push("Links not allowed");
    if (!constraints.allow_product_mention) known.push("No self-promotion");
    if (constraints.promo_ratio_rule) known.push("Promotion ratio rule");
    if (constraints.flair_required) known.push("Flair required");
    if (constraints.karma_or_age_gate) known.push("Karma or account-age gate");
  }

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <a
            href={rulesUrl(subreddit)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded-full border border-border bg-panel px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition hover:text-ink"
          >
            <Info className="h-3 w-3" />
            {known.length ? known[0] : "Check posting rules"}
          </a>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-[12px] leading-snug">
          <p>{RULES_REMINDER}</p>
          {known.length > 0 && (
            <p className="mt-1.5">
              r/{subreddit} rules on file: {known.join(" • ")}.
            </p>
          )}
          <p className="mt-1.5 text-muted-foreground">Opens r/{subreddit}&apos;s rules page.</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
